import React, { useMemo, useState } from 'react';
import { Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from 'shared/utils/common.utils';
import { format } from 'date-fns';
import NoData from 'shared/design-system/components/no-data-box';
import useGetEmittedBills from './_hooks/useGetEmittedBills';

interface Props {
  projectId: number;
  readOnly?: boolean;
}

type Bill = ReturnType<typeof useGetEmittedBills>['emittedBills'] extends Array<infer T> | undefined ? T : never;

const fmtDate = (d?: Date | string | null) => (d ? format(new Date(d as any), 'dd/MM/yy') : '-');

const COLSPAN = 12;

const EmittedBills: React.FC<Props> = ({ projectId, readOnly = false }) => {
  const { t } = useTranslation();
  const { emittedBills } = useGetEmittedBills(projectId);
  void readOnly;

  // Raggruppamento per pkFattura
  const groups = useMemo(() => {
    const map = new Map<string, Bill[]>();
    (emittedBills ?? []).forEach((b) => {
      const key = String(b?.pkFattura ?? '__no_pk__');
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(b as Bill);
    });
    return Array.from(map.entries()).map(([pkFattura, rows]) => ({
      pkFattura,
      rows,
      totale: rows.reduce((acc, r: any) => acc + (Number(r?.imponibile) || 0), 0),
      first: rows[0] as any,
    }));
  }, [emittedBills]);

  const grandTotal = useMemo(
    () => (emittedBills ?? []).reduce((acc, r: any) => acc + (Number(r?.imponibile) || 0), 0),
    [emittedBills],
  );

  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const toggle = (key: string) => setExpanded((p) => ({ ...p, [key]: !p[key] }));

  if (!emittedBills?.length) {
    return (
      <div className="d-flex justify-content-center align-items-center">
        <NoData msg={t('project-detail.billing-plan.no-invoices')} />
      </div>
    );
  }

  return (
    <div style={{ overflowY: 'auto', height: 'calc(100vh - 315px)' }}>
      <Table bordered>
        <thead>
          <tr>
            <th className="align-content-center" style={{ width: 40 }} />
            <th className="align-content-center">{t('Bill ID')}</th>
            <th className="align-content-center">{t('N. bill')}</th>
            <th className="align-content-center">{t('Amount')}</th>
            <th className="align-content-center">{t('Date')}</th>
            <th className="align-content-center">{t('Expiring date')}</th>
            <th className="align-content-center">{t('Payment date')}</th>
            <th className="align-content-center">{t('Type')}</th>
            <th className="align-content-center">{t('Account')}</th>
            <th className="align-content-center">{t('Description')}</th>
            <th className="align-content-center">{t('Accounting status')}</th>
            <th className="align-content-center">{t('Status')}</th>
          </tr>
        </thead>
        <tbody>
          {groups.map((g) => {
            const isOpen = !!expanded[g.pkFattura];
            const f = g.first;
            return (
              <React.Fragment key={`grp-${g.pkFattura}`}>
                <tr style={{ backgroundColor: '#f7f8fb', fontWeight: 600 }}>
                  <td className="text-center">
                    <button
                      type="button"
                      onClick={() => toggle(g.pkFattura)}
                      className="btn-icon"
                      title={isOpen ? t('Comprimi') : t('Espandi')}
                      style={{ background: 'transparent', border: 'none' }}
                    >
                      <i className={`fas fa-${isOpen ? 'minus' : 'plus'}`} />
                    </button>
                  </td>
                  <td>{f?.pkFattura ?? '-'}</td>
                  <td>{f?.numeroFattura ?? '-'}</td>
                  <td className="text-end">{`${formatCurrency(g.totale)}€`}</td>
                  <td>{fmtDate(f?.dataFattura)}</td>
                  <td>{fmtDate(f?.dataScadenza)}</td>
                  <td>{fmtDate(f?.dataPagamento)}</td>
                  <td>{f?.tipoFattura ?? '-'}</td>
                  <td>{f?.cliente ?? '-'}</td>
                  <td title={f?.descrizione || '-'}>
                    {f?.descrizione
                      ? `${f.descrizione.slice(0, 100)}${f.descrizione.length > 100 ? '…' : ''}`
                      : '-'}
                  </td>
                  <td>{f?.fattStatoContabile ?? '-'}</td>
                  <td>{f?.fattStato ?? '-'}</td>
                </tr>
                {isOpen &&
                  g.rows.map((b: any) => (
                    <tr key={`row-${b?.idRowFattura}`}>
                      <td className="text-end text-muted" style={{ fontSize: '0.78rem' }}>
                        #{b?.idRowFattura}
                      </td>
                      <td>{b?.pkFattura ?? '-'}</td>
                      <td>{b?.numeroFattura ?? '-'}</td>
                      <td className="text-end">{`${formatCurrency(b?.imponibile)}€`}</td>
                      <td>{fmtDate(b?.dataFattura)}</td>
                      <td>{fmtDate(b?.dataScadenza)}</td>
                      <td>{fmtDate(b?.dataPagamento)}</td>
                      <td>{b?.tipoFattura ?? '-'}</td>
                      <td>{b?.cliente ?? '-'}</td>
                      <td title={b?.descrizione || '-'}>
                        {b?.descrizione
                          ? `${b.descrizione.slice(0, 100)}${b.descrizione.length > 100 ? '…' : ''}`
                          : '-'}
                      </td>
                      <td>{b?.fattStatoContabile ?? '-'}</td>
                      <td>{b?.fattStato ?? '-'}</td>
                    </tr>
                  ))}
              </React.Fragment>
            );
          })}
        </tbody>
        <tfoot>
          <tr style={{ backgroundColor: '#eef2fb', fontWeight: 700 }}>
            <td colSpan={3} className="text-end">
              {t('Total')}
            </td>
            <td className="text-end">{`${formatCurrency(grandTotal)}€`}</td>
            <td colSpan={COLSPAN - 4} />
          </tr>
        </tfoot>
      </Table>
    </div>
  );
};

export default EmittedBills;
